import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import Countdown from 'react-countdown';
import { FiDownload } from 'react-icons/fi';
import { FiVideo } from 'react-icons/fi';
import { HiOutlineCloudUpload, HiOutlinePlus } from 'react-icons/hi';
import { Link } from 'react-router-dom';

import { applyFilters } from '@/components/filters/ShopifyFilterUtils';
import {
	FilterState,
	ShopifyFilters,
} from '@/components/filters/ShopifyFilters';
import CreateProductModal from '@/components/modal/product/CreateProductModal';
import CsvUploadModal from '@/components/modal/product/CsvUploadModal';
import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import ProductViewModal from '@/components/modal/store/ProductViewModal';
import PaginationBottomTable from '@/components/table/common/PaginationBottomTable';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { CSV_TEMPLATE_PATH } from '@/components/utils/constants';
import { useModal } from '@/hooks/useModal';
import { useUser } from '@/hooks/useUser';
import { useUserStore } from '@/hooks/useUserStore';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { InventoryType } from '@/interfaces/api/InventoryType';
import {
	ICollectionProductId,
	IProductIdWithHaggle,
} from '@/interfaces/api/product/IProductIdWithHaggle';
import { haggleApiService } from '@/services/haggle.service';

const StoreProductsDashboard = () => {
	const { store } = useUserStore();
	const { user } = useUser();

	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [pageCursor, setPageCursor] = useState<string | null>(null);
	const [pageBeforeCursor, setPageBeforeCursor] = useState<string | null>(null);

	const {
		data: products,
		isLoading: productsIsLoading,
		refetch: refetchProducts,
	} = useQuery<ICollectionProductId>({
		queryKey: [
			ApiQueryKeys.getAllStoreProductsWithHaggle,
			pageSize,
			pageNumber,
			pageCursor,
			pageBeforeCursor,
			user?.inventoryType,
		],
		queryFn: async () => {
			const products = await haggleApiService.getAllStoreProductsWithHaggle({
				params: {
					'page[size]': pageSize,
					'page[number]': pageNumber,
					'page[cursor]': pageCursor,
					'page[beforeCursor]': pageBeforeCursor,
					includeHaggleDetails: false,
				},
			});
			return products;
		},
		refetchOnWindowFocus: false,
		enabled: !!user,
	});

	const [selectedProductId, setSelectedProductId] = useState<number | null>(
		null,
	);
	const [filteredProducts, setFilteredProducts] = useState<
		IProductIdWithHaggle[]
	>([]);

	const handleViewProduct = (product: IProductIdWithHaggle) => {
		setSelectedProductId(product.id);
		openInviteMerchantModal();
	};

	const {
		modal: viewProductModal,
		openModal: openInviteMerchantModal,
		closeModal: handleCloseModal,
	} = useModal({
		children: (
			<ProductViewModal
				closeModal={() => handleCloseModal()}
				productId={selectedProductId}
				refetchProducts={refetchProducts}
			/>
		),
		closeOnOutClick: false,
		modalBoxClassName: 'max-w-6xl md:min-w-[500px]',
	});

	const handleFilterChange = (filters: FilterState) => {
		if (products?.data) {
			const productsWithHaggle = products.data;
			setFilteredProducts(applyFilters(productsWithHaggle, filters));
		}
	};

	useEffect(() => {
		if (products?.data) {
			const productsWithHaggle = products.data;
			setFilteredProducts(productsWithHaggle);
		} else {
			setFilteredProducts([]);
		}
	}, [products]);

	const {
		modal: csvUploadModal,
		openModal: openCsvUploadModal,
		closeModal: closeCsvUploadModal,
	} = useModal({
		children: (
			<CsvUploadModal
				closeModal={() => {
					refetchProducts();
					return closeCsvUploadModal();
				}}
			/>
		),
		closeOnOutClick: false,
		modalBoxClassName: 'max-w-4xl',
	});

	const {
		modal: productFormModal,
		openModal: openProductFormModal,
		closeModal: closeProductFormModal,
	} = useModal({
		children: (
			<CreateProductModal
				defaultClose={() => closeProductFormModal()}
				refetchProducts={refetchProducts}
				store={store}
			/>
		),
		closeOnOutClick: false,
		modalBoxClassName: 'max-w-4xl',
	});

	const handleAddProduct = () => {
		openProductFormModal();
	};

	const handlePageNumberChange = useCallback(
		(newPageNumber: number) => {
			if (!products) return;
			setPageNumber(newPageNumber);
			if (newPageNumber > pageNumber && products.endCursor) {
				// Going forward
				setPageBeforeCursor(null);
				setPageCursor(products.endCursor);
			} else if (newPageNumber < pageNumber && products.startCursor) {
				// Going backward
				setPageBeforeCursor(products.startCursor);
				setPageCursor(null);
			}
		},
		[products, pageNumber],
	);

	const handlePaginationOptions = (): IPaginationTable => {
		return {
			pageSize: products?.pageSize ?? pageSize,
			pageNumber: products?.pageNumber ?? pageNumber,
			pageCount:
				products?.pageCount ?? Math.ceil((products?.itemCount ?? 0) / pageSize),
			itemCount: products?.itemCount ?? 0,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber: handlePageNumberChange,
		};
	};

	const displayProducts = (products?.data as IProductIdWithHaggle[]) || [];

	if (
		user?.inventoryType !== InventoryType.SHOPIFY &&
		products?.itemCount === 0
	) {
		return (
			<div className="flex flex-col p-6">
				{csvUploadModal}
				{productFormModal}
				<div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-2 mb-5">
					<Link
						to="/user/dashboard/how-to-videos"
						className="px-4 py-2 text-sm text-white rounded-lg bg-blue-500 hover:bg-blue-600 active:bg-purpleBrand-900 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
					>
						<FiVideo className="w-5 h-5" />
						How to Create Inventory CSV
					</Link>
					<a
						href={CSV_TEMPLATE_PATH}
						download
						tabIndex={0}
						aria-label="Download CSV template"
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								(e.target as HTMLAnchorElement).click();
							}
						}}
						className="px-4 py-2 text-sm text-white rounded-lg bg-purpleBrand-500 hover:bg-purpleBrand-600 active:bg-purpleBrand-900 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
					>
						<FiDownload className="w-5 h-5" />
						Download CSV Template
					</a>
				</div>
				<div className="mb-6">
					<h2 className="text-xl font-bold text-gray-900">
						{store?.tradeName} Products
					</h2>
				</div>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-h-[300px]">
					{/* CSV Upload Card */}
					<div className="flex flex-col items-center justify-center bg-white rounded-2xl shadow p-8">
						<HiOutlineCloudUpload
							className="text-purpleBrand-500 text-6xl mb-4"
							aria-hidden="true"
						/>
						<h2 className="text-xl font-bold text-gray-900 mb-2">
							Upload Products via CSV
						</h2>
						<p className="text-gray-600 mb-6 text-center max-w-md">
							Have multiple products? Upload them all at once using a CSV file
							for quick setup.
						</p>
						<button
							onClick={openCsvUploadModal}
							className="bg-purpleBrand-500 hover:bg-purpleBrand-600 active:bg-purpleBrand-900 text-white font-semibold px-6 py-3 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-purpleBrand-400"
							aria-label="Upload products via CSV"
						>
							Upload CSV
						</button>
					</div>

					{/* Individual Product Card */}
					<div className="flex flex-col items-center justify-center bg-white rounded-2xl shadow p-8">
						<HiOutlinePlus
							className="text-purpleBrand-500 text-6xl mb-4"
							aria-hidden="true"
						/>
						<h2 className="text-xl font-bold text-gray-900 mb-2">
							Add Individual Product
						</h2>
						<p className="text-gray-600 mb-6 text-center max-w-md">
							Don't have a CSV? No problem! Add your products one by one using
							our simple form.
						</p>
						<button
							onClick={handleAddProduct}
							className="bg-purpleBrand-500 hover:bg-purpleBrand-600 active:bg-purpleBrand-900 text-white font-semibold px-6 py-3 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-purpleBrand-400 flex items-center gap-2"
							aria-label="Add single product"
						>
							Add Single Product
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col p-6">
			{csvUploadModal}
			{productFormModal}
			<div className="mb-6">
				<ShopifyFilters
					products={displayProducts}
					onFilterChange={handleFilterChange}
				/>
			</div>
			{/* CSV Upload Button */}
			{user?.inventoryType !== InventoryType.SHOPIFY && (
				<div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-2 mb-5">
					<Link
						to="/user/dashboard/how-to-videos"
						className="px-4 py-2 text-sm text-white rounded-lg bg-blue-500 hover:bg-blue-600 active:bg-purpleBrand-900 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
					>
						<FiVideo className="w-5 h-5" />
						How to Create Inventory CSV
					</Link>
					<a
						href={CSV_TEMPLATE_PATH}
						download
						tabIndex={0}
						aria-label="Download CSV template"
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								(e.target as HTMLAnchorElement).click();
							}
						}}
						className="px-4 py-2 text-sm text-white rounded-lg bg-purpleBrand-500 hover:bg-purpleBrand-600 active:bg-purpleBrand-900 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
					>
						<FiDownload className="w-5 h-5" />
						Download CSV Template
					</a>

					<button
						onClick={openCsvUploadModal}
						className="px-4 py-2 text-sm text-white rounded-lg bg-purpleBrand-500 hover:bg-purpleBrand-600 active:bg-purpleBrand-900 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
					>
						<HiOutlineCloudUpload className="h-5 w-5" />
						Update Products via CSV
					</button>
					<button
						onClick={handleAddProduct}
						className="px-4 py-2 text-sm text-white rounded-lg bg-purpleBrand-500 hover:bg-purpleBrand-600 active:bg-purpleBrand-900 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
					>
						<HiOutlinePlus className="h-5 w-5" />
						Add New Product
					</button>
				</div>
			)}
			<div className="flex-1 w-full">
				{productsIsLoading && <LoadingSkeletonModalTable />}
				{products?.data && !productsIsLoading && (
					<div data-test="product-list" className="w-full">
						<div className="flex gap-[50px]">
							<h2 className="text-xl font-bold text-gray-900">
								{store?.tradeName} Products
							</h2>
						</div>
						<div className="flex flex-wrap gap-4 py-4 w-full justify-center md:justify-start">
							{filteredProducts.map((product) => (
								<button
									onClick={() => handleViewProduct(product)}
									key={product.id}
									className="relative flex flex-col gap-3 rounded-lg hover:border-2 hover:border-gray-300 w-full max-w-[216px] min-w-[200px] sm:w-[216px] sm:min-w-[216px] flex-shrink-0"
									data-test="product-card"
								>
									<div className="relative rounded-lg">
										<div className="w-full overflow-hidden rounded-lg">
											<img
												alt={product.image?.alt ?? `${product.title} image`}
												src={
													product.image?.src ||
													'data:image/svg+xml;charset=UTF-8,' +
														encodeURIComponent(`
														<svg xmlns="http://www.w3.org/2000/svg" width="216" height="144" viewBox="0 0 216 144">
														<rect width="100%" height="100%" fill="#e5e7eb" />
														<text x="50%" y="50%" fill="#6b7280" font-size="14" font-family="Arial, Helvetica, sans-serif"
																text-anchor="middle" dominant-baseline="middle">
															No Image
														</text>
														</svg>
													`)
												}
												className="object-cover h-72 object-center w-full"
											/>
											{product.hasHaggle && (
												<div
													className="absolute top-2 left-2 z-20 pointer-events-none rounded-lg"
													data-test="brand-logo"
												>
													{product.haggleStartDate &&
													new Date(product.haggleStartDate) > new Date() ? (
														<div className="relative w-[100px] h-[40px]">
															{/* Subdued "off" effect for Coming Soon */}
															<div className="absolute inset-0 bg-[#f08080] rounded-lg opacity-40"></div>
															{/* Main banner */}
															<div className="relative bg-gradient-to-r from-[#f08080] via-[#e07070] to-[#d06060] text-white px-3 py-2 rounded-lg shadow-md border border-[#d06060] h-full flex items-center justify-center opacity-80">
																<div className="flex items-center justify-center">
																	<div className="w-1.5 h-1.5 bg-gray-300 rounded-full mr-1.5"></div>
																	<span className="text-xs font-bold tracking-wide">
																		COMING SOON
																	</span>
																</div>
															</div>
														</div>
													) : (
														<div className="relative w-[100px] h-[40px]">
															{/* Outward glowing effect for Live */}
															<div className="absolute inset-0 bg-yellow-400 rounded-lg blur-md animate-pulse opacity-70"></div>
															<div className="absolute inset-0 bg-yellow-300 rounded-lg blur-sm animate-pulse opacity-80"></div>
															{/* Main banner */}
															<div className="relative bg-yellow-500 text-white px-3 py-2 rounded-lg shadow-lg border border-yellow-300 h-full flex items-center justify-center">
																<div className="flex items-center justify-center">
																	<div className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse"></div>
																	<span className="text-xs font-bold tracking-wide">
																		LIVE
																	</span>
																</div>
															</div>
														</div>
													)}
												</div>
											)}
											{product.haggle &&
												product.haggleStartDate &&
												new Date(product.haggleStartDate) > new Date() && (
													<div className="absolute bottom-0 left-0 right-0 p-2 text-sm font-semibold text-white rounded-b-lg bg-black/50">
														Starts in:{' '}
														<Countdown
															date={new Date(product.haggleStartDate)}
															renderer={({
																days,
																hours,
																minutes,
																seconds,
															}: {
																days: number;
																hours: number;
																minutes: number;
																seconds: number;
															}) => {
																if (days > 0) {
																	return `${days}d ${hours}h`;
																} else if (hours > 1) {
																	return `${hours}h ${minutes}m`;
																} else {
																	return `${minutes}m ${seconds}s`;
																}
															}}
														/>
													</div>
												)}
										</div>
									</div>
									<div className="">
										<h3 className="text-sm font-medium text-gray-900">
											{product.title}
										</h3>
									</div>
								</button>
							))}
						</div>
						<PaginationBottomTable {...handlePaginationOptions()} />
					</div>
				)}

				{viewProductModal}
			</div>
		</div>
	);
};
export default StoreProductsDashboard;
